// src/progressoverview.js

import React, { useMemo, useEffect, useState } from "react";
import { HEROES, resolveProgram } from "./programs";

const ProgressOverview = ({
  progress,
  daysPerWeekByHero,
  log,
  blockLengthByHero,
}) => {
  const heroStats = useMemo(() => {
    const stats = [];

    HEROES.forEach((hero) => {
      const program = resolveProgram(hero.programType);
      const programDays = program.days || [];
      const defaultWeeks = program.weeks || 9;
      const weeks =
        blockLengthByHero &&
        blockLengthByHero[hero.id] &&
        [9, 12].includes(blockLengthByHero[hero.id])
          ? blockLengthByHero[hero.id]
          : defaultWeeks;

      const daysPerWeek =
        daysPerWeekByHero[hero.id] ||
        program.defaultDaysPerWeek ||
        Math.min(5, programDays.length);

      const visibleDays = programDays.slice(0, daysPerWeek);
      if (!visibleDays.length) return;

      const heroProg = progress[hero.id] || {};
      const heroLog = log[hero.id] || {};

      let completed = 0;
      const total = weeks * visibleDays.length;
      let hasProgress = false;
      const weekly = [];

      for (let w = 1; w <= weeks; w++) {
        const weekProg = heroProg[w] || {};

        const dayDoneFlags = visibleDays.map((d) => {
          const done = !!weekProg[d.id];
          if (done) {
            completed += 1;
            hasProgress = true;
          }
          return done;
        });

        const weekCompleted = dayDoneFlags.filter(Boolean).length;
        const weekTotal = visibleDays.length;
        const weekPct =
          weekTotal > 0 ? Math.round((weekCompleted / weekTotal) * 100) : 0;

        weekly.push({
          week: w,
          completed: weekCompleted,
          total: weekTotal,
          pct: weekPct,
          days: dayDoneFlags,
        });
      }

      // Also count log-only usage as "started"
      const heroHasLog = (() => {
        if (!heroLog) return false;
        return Object.values(heroLog).some((weekLog) =>
          Object.values(weekLog || {}).some((dayLog) =>
            Object.values(dayLog || {}).some((entry) => {
              if (!entry) return false;
              const sets = entry.sets || [];
              const rpe = entry.rpe;
              const hasRpe = rpe && String(rpe).trim() !== "";
              const hasSets =
                sets.length > 0 &&
                sets.some(
                  (s) =>
                    (s.weight && String(s.weight).trim() !== "") ||
                    (s.reps && String(s.reps).trim() !== "") ||
                    (s.rpe && String(s.rpe).trim() !== "")
                );
              return hasRpe || hasSets;
            })
          )
        );
      })();

      if (!hasProgress && !heroHasLog) return;

      const pct = total ? Math.round((completed / total) * 100) : 0;

      const timeline = [];
      for (let w = 1; w <= weeks; w++) {
        const weekProg = heroProg[w] || {};
        visibleDays.forEach((d, index) => {
          const done = !!weekProg[d.id];
          timeline.push({
            week: w,
            dayOrder: index,
            done,
          });
        });
      }

      let streak = 0;
      for (let i = timeline.length - 1; i >= 0; i--) {
        if (!timeline[i].done) {
          if (streak === 0) continue;
          break;
        }
        streak += 1;
      }

      const daysCompletedSoFar = completed;
      const daysPlannedTotal = total;
      const daysLeft =
        daysPlannedTotal > daysCompletedSoFar
          ? daysPlannedTotal - daysCompletedSoFar
          : 0;

      stats.push({
        hero,
        program,
        completed,
        total,
        pct,
        weekly,
        daysCompletedSoFar,
        daysLeft,
        streak,
        daysPerWeek,
      });
    });

    return stats;
  }, [progress, daysPerWeekByHero, log, blockLengthByHero]);

  const [selectedHeroId, setSelectedHeroId] = useState(null);

  useEffect(() => {
    if (!heroStats.length) return;
    if (
      !selectedHeroId ||
      !heroStats.some((h) => h.hero.id === selectedHeroId)
    ) {
      setSelectedHeroId(heroStats[0].hero.id);
    }
  }, [heroStats, selectedHeroId]);

  if (!heroStats.length) {
    return (
      <div className="card">
        <div className="card-header">
          <div className="card-title">Progress Overview</div>
          <div className="tag">No data yet</div>
        </div>
        <p className="program-desc">
          You haven’t logged any workouts or progress yet. Start a hero program
          in the Workouts tab and mark days as done or log sets to see your
          momentum and completion here.
        </p>
      </div>
    );
  }

  const primary =
    heroStats.find((h) => h.hero.id === selectedHeroId) || heroStats[0];

  const streakFlame =
    primary.streak >= 7 ? "🔥🔥" : primary.streak >= 3 ? "🔥" : "";

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Progress Overview</div>
        <div className="tag">Momentum + Completion</div>
      </div>

      <div className="momentum-section">
        <div className="momentum-header">
          <div>
            <div className="momentum-title">Program Momentum</div>
            <div className="momentum-sub">
              Each vertical bar is a week. Green = done, Grey = not completed
              yet.
            </div>
          </div>
          <select
            className="momentum-select"
            value={primary.hero.id}
            onChange={(e) => setSelectedHeroId(e.target.value)}
          >
            {heroStats.map((h) => (
              <option key={h.hero.id} value={h.hero.id}>
                {h.hero.name}
              </option>
            ))}
          </select>
        </div>

        {/* AUTO-STRETCH WEEKS ACROSS WIDTH, BIGGER TALL BARS */}
        {/* GRID WITH LEFT-SIDE DAY LABELS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `40px repeat(${primary.weekly.length}, minmax(0, 1fr))`,
            columnGap: 8,
            padding: "20px 10px",
            marginTop: 10,
          }}
        >
          {/* LEFT AXIS: DAY LABELS (D1–D4 or D1–D5) */}
          <div
            style={{
              display: "flex",
              flexDirection: "column-reverse",
              justifyContent: "space-between",
              height: 140,
              paddingRight: 2,
              fontSize: "0.8rem",
              opacity: 0.85,
            }}
          >
            {Array.from({ length: primary.daysPerWeek }).map((_, idx) => (
              <div key={idx}>D{idx + 1}</div>
            ))}
          </div>

          {/* WEEK COLUMNS */}
          {primary.weekly.map((w) => {
            return (
              <div
                key={w.week}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <div
                  style={{
                    height: 140,
                    width: 28,
                    borderRadius: 999,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column-reverse",
                    background:
                      "linear-gradient(180deg, rgba(15,23,42,0.9), rgba(15,23,42,0.6))",
                    padding: 2,
                    boxSizing: "border-box",
                    gap: 2,
                  }}
                >
                  {[...w.days].map((done, idx) => (
                    <div
                      key={idx}
                      style={{
                        flex: 1,
                        borderRadius: 6,
                        background: done
                          ? "rgba(34,197,94,0.9)"
                          : "rgba(148,163,184,0.25)",
                      }}
                    />
                  ))}
                </div>
                <div className="momentum-vertical-label">W{w.week}</div>
              </div>
            );
          })}
        </div>

        <div className="momentum-stats-row">
          <span className="progress-chip">
            {primary.daysCompletedSoFar}/{primary.total} days done
          </span>
          <span className="progress-chip secondary">
            {primary.daysLeft} days left
          </span>
          <span className="progress-chip">
            Current streak: {primary.streak} day
            {primary.streak === 1 ? "" : "s"}
            {streakFlame && <> {streakFlame}</>}
          </span>
        </div>
      </div>

      <div className="section-label">All hero programs you’ve started</div>
      <div className="progress-list">
        {heroStats.map((row) => (
          <div key={row.hero.id} className="progress-row-item">
            <div className="progress-row-main">
              <div className="progress-hero-name">{row.hero.name}</div>
              <div className="progress-hero-sub">
                {row.hero.universe} • {row.program.name}
              </div>
            </div>
            <div className="progress-hero-stats">
              <span className="progress-chip">
                {row.completed}/{row.total} days
              </span>
              <span className="progress-chip secondary">
                {row.pct}% complete
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgressOverview;
